/**
 * Embed protocol — typed postMessage contract between the host page
 * (console / a customer site) and the embedded `@trustsy/verify` flow running in
 * a cross-origin iframe.
 *
 * The verify app is embedded cross-origin because it needs the camera and
 * WebAuthn, which the host grants via the iframe's `allow` attribute. The
 * recommended policy string (host side) is:
 *
 *   allow="camera *; microphone *; publickey-credentials-get *; publickey-credentials-create *"
 *
 * (`*` here is the Permissions-Policy allowlist meaning "any origin the iframe
 * navigates to" — it is NOT a postMessage targetOrigin. See the strict origin
 * checks in `createEmbedChannel` below: we never send with '*'.)
 */

/** Messages sent FROM the embedded verify app TO the host page. */
export type EmbedMessage =
  | { type: 'trustsy:ready' }
  | { type: 'trustsy:state'; state: string }
  | { type: 'trustsy:done'; status: 'approved' | 'rejected' | 'manual_review' }
  | { type: 'trustsy:error'; code: string };

export type EmbedMessageType = EmbedMessage['type'];

/** The `status` payload of a `trustsy:done` message. */
export type EmbedDoneStatus = Extract<
  EmbedMessage,
  { type: 'trustsy:done' }
>['status'];

const EMBED_MESSAGE_TYPES: readonly EmbedMessageType[] = [
  'trustsy:ready',
  'trustsy:state',
  'trustsy:done',
  'trustsy:error',
];

const DONE_STATUSES: readonly EmbedDoneStatus[] = [
  'approved',
  'rejected',
  'manual_review',
];

/**
 * Runtime type guard. Untrusted `postMessage` data is `unknown`; validate the
 * shape before trusting the discriminant. Returns false for anything that is
 * not a well-formed EmbedMessage — callers drop the frame.
 */
export const isEmbedMessage = (data: unknown): data is EmbedMessage => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const record = data as Record<string, unknown>;
  const type = record.type;

  if (typeof type !== 'string' || !EMBED_MESSAGE_TYPES.includes(type as EmbedMessageType)) {
    return false;
  }

  switch (type) {
    case 'trustsy:ready':
      return true;
    case 'trustsy:state':
      return typeof record.state === 'string';
    case 'trustsy:done':
      return (
        typeof record.status === 'string' &&
        DONE_STATUSES.includes(record.status as EmbedDoneStatus)
      );
    case 'trustsy:error':
      return typeof record.code === 'string';
    default:
      return false;
  }
};

/**
 * `https:`, or `http:` on localhost for development — the only origins either
 * side of the embed or the web SDK talks to.
 */
export const isSecureOrigin = (url: URL): boolean => {
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  return url.protocol === 'https:' || (url.protocol === 'http:' && local);
};

export interface EmbedChannel {
  /** Post a typed message to the target window at the pinned origin. */
  send: (message: EmbedMessage) => void;
  /**
   * Subscribe to inbound messages that originate from the pinned origin AND
   * pass the runtime shape guard. Returns an unsubscribe function.
   */
  subscribe: (handler: (message: EmbedMessage) => void) => () => void;
  /** Remove the internal message listener (idempotent). */
  close: () => void;
}

/**
 * Create a strictly-origin-checked postMessage channel.
 *
 * @param targetWindow the window to post to (e.g. `iframe.contentWindow` on the
 *        host side, or `window.parent` on the embedded side).
 * @param targetOrigin the exact origin to send to and to accept from. MUST be a
 *        concrete origin like "https://console.example.com" — passing '*' is a
 *        cross-origin data-leak footgun and is rejected at construction time.
 */
export const createEmbedChannel = (
  targetWindow: Window,
  targetOrigin: string,
): EmbedChannel => {
  if (targetOrigin === '*' || targetOrigin.trim() === '') {
    throw new Error(
      "createEmbedChannel: targetOrigin must be a concrete origin, never '*' or empty — " +
        'a wildcard would broadcast KYC state to any embedding site.',
    );
  }

  const send = (message: EmbedMessage): void => {
    // Pinned origin on every send — the browser drops the message if the
    // target window's origin does not match, so state never leaks.
    targetWindow.postMessage(message, targetOrigin);
  };

  const listeners = new Set<(message: EmbedMessage) => void>();

  const onMessage = (event: MessageEvent): void => {
    // Reject frames from any other origin AND any frame not sent by our peer.
    if (event.origin !== targetOrigin || event.source !== targetWindow) {
      return;
    }

    if (!isEmbedMessage(event.data)) {
      return;
    }

    for (const handler of listeners) {
      handler(event.data);
    }
  };

  let attached = false;

  const ensureAttached = (): void => {
    if (!attached) {
      window.addEventListener('message', onMessage);
      attached = true;
    }
  };

  const close = (): void => {
    if (attached) {
      window.removeEventListener('message', onMessage);
      attached = false;
    }
    listeners.clear();
  };

  const subscribe = (handler: (message: EmbedMessage) => void): (() => void) => {
    ensureAttached();
    listeners.add(handler);

    return () => {
      listeners.delete(handler);
    };
  };

  return { send, subscribe, close };
};
